<template>
  <AdaptiveSheet
    :opened="opened"
    @update:opened="$emit('update:opened', $event)"
    :close-on-escape="true"
    :tablet-fullscreen="false"
    class="neg-sheet"
    :swipe-to-close="true"
  >
    <f7-page-content>
      <div class="neg-sheet-header">
        <f7-block-title large>{{ $t('negotiation.negotiatePrice') }}</f7-block-title>
        <p v-if="productName" class="neg-product-name">{{ productName }}</p>
        <div class="neg-current-price" v-if="currentPrice">
          <span class="neg-price-label">{{ $t('negotiation.listedPrice') }}</span>
          <span class="neg-price-value">{{ formatPrice(currentPrice,settings.currency) }}</span>
        </div>
      </div>

      <f7-list inset strong list-media outline>
        <f7-list-input
          :label="$t('negotiation.yourOffer')"
          type="text"
          inputmode="numeric"
          :value="priceInput"
          @input="onPriceInput"
          :placeholder="$t('negotiation.enterPrice')"
        >
          <template #media>
            <span class="neg-currency">{{settings.currency}}</span>
          </template>
        </f7-list-input>
        <f7-list-input
          :label="$t('negotiation.messageOptional')"
          type="textarea"
          :value="message"
          @input="message = $event.target.value"
          :placeholder="$t('negotiation.explainOffer')"
          :rows="3"
        />
      </f7-list>

      <div class="neg-suggestion-row" v-if="currentPrice">
        <span class="neg-suggestion-label">{{ $t('negotiation.quickOffer') }}</span>
        <div class="neg-suggestions">
          <f7-button
            v-for="pct in [10, 15, 20, 25]"
            :key="pct"
            small
            outline
            @click="applyDiscount(pct)"
          >
            -{{ pct }}%
          </f7-button>
        </div>
      </div>

      <f7-block v-if="settings && settings.minimumOffer > 0" class="neg-info">
        <f7-icon f7="info_circle" size="14" color="orange" />
        <span>{{ $t('negotiation.minimumOffer') }} {{ formatPrice(settings.minimumOffer, settings.currency) }}</span>
      </f7-block>

      <f7-block>
        <f7-button
          large
          fill
          round
          raised
          :disabled="!isValid"
          @click="submitOffer"
          class="neg-submit-btn"
        >
          {{ $t('negotiation.sendOffer') }}
        </f7-button>
      </f7-block>
    </f7-page-content>
  </AdaptiveSheet>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { f7 } from 'framework7-vue'
import { useMarketplaceStore, NEGOTIATION_SETTINGS_DEFAULTS } from '../../js/pinia-stores/marketplace.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import i18n from '../../js/i18n.js'

const props = defineProps({
  opened: { type: Boolean, default: false },
  productId: { type: String, default: '' },
  productName: { type: String, default: '' },
  currentPrice: { type: Number, default: 0 },
  sellerId: { type: String, default: '' },
  sellerName: { type: String, default: '' },
  groupId: { type: String, default: '' },
})

const emit = defineEmits(['update:opened', 'offer-submitted'])

const { t } = i18n.global
const store = useMarketplaceStore()
const keycloakStore = useKeycloakStore()

const priceInput = ref('')
const message = ref('')

const settings = computed(() =>
  props.productId ? store.getNegotiationSettings(props.productId) : NEGOTIATION_SETTINGS_DEFAULTS
)

const numericPrice = computed(() => {
  const cleaned = priceInput.value.replace(/\D/g, '')
  return cleaned ? parseInt(cleaned, 10) : 0
})

const isValid = computed(() => {
  if (!numericPrice.value || numericPrice.value <= 0) return false
  if (settings.value.minimumOffer > 0 && numericPrice.value < settings.value.minimumOffer) return false
  return true
})

function onPriceInput(e) {
  priceInput.value = e.target.value.replace(/[^\d]/g, '')
}

function applyDiscount(pct) {
  if (!props.currentPrice) return
  const discounted = Math.round(props.currentPrice * (1 - pct / 100))
  priceInput.value = String(discounted)
}

function formatPrice(amount) {
  if (!amount) return '0 CFA'
  return `${Math.round(amount).toLocaleString('fr-FR')} CFA`
}

function submitOffer() {
  if (!isValid.value) return
  const userId = keycloakStore.subject || ''
  const userName = keycloakStore.userInfo?.name || keycloakStore.userInfo?.given_name || ''

  const offer = store.createNegotiation({
    productId: props.productId,
    buyerId: userId,
    buyerName: userName,
    sellerId: props.sellerId,
    sellerName: props.sellerName,
    groupId: props.groupId,
    productName: props.productName,
    offeredPrice: numericPrice.value,
    buyerMessage: message.value.trim(),
  })

  emit('offer-submitted', offer)
  emit('update:opened', false)
  priceInput.value = ''
  message.value = ''
  f7.toast.show({ text: t('negotiation.offerSent'), closeTimeout: 1500 })
}

watch(() => props.opened, (v) => {
  if (!v) {
    priceInput.value = ''
    message.value = ''
  }
})
</script>

<style scoped>
.neg-sheet-header { padding: 16px 16px 0; }
.neg-product-name { font-size: 14px; font-weight: 500; margin: 4px 0 0; color: var(--f7-text-color); }
.neg-current-price {
  display: flex; align-items: baseline; gap: 8px; margin-top: 8px;
}
.neg-price-label { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.neg-price-value { font-size: 20px; font-weight: 600; color: var(--f7-theme-color); }
.neg-currency { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.neg-suggestion-row { padding: 0 16px; }
.neg-suggestion-label { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); }
.neg-suggestions { display: flex; gap: 6px; margin-top: 6px; }
.neg-info {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93);
  padding: 0 16px;
}
.neg-submit-btn { margin-top: 4px; }
</style>
